import { useEffect, useRef, useState } from "react";
import Icon from "@/components/icon";
import {
  GENERATION_STARTS_AT,
  waitingCopy,
} from "@/components/report/report-progress-copy";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";

// Waiting UX for report generation. Named-step checklist, elapsed timer,
// honest ETA, and a soft stuck state when generation overruns a client budget.
// Active phase is derived from the numeric progress (milestones documented in
// report-progress-copy.ts), not Polish step strings.

// The backend function is killed at 540 s and fails the doc itself before that
// (settings.drug_search_deadline_seconds), so a longer client budget only
// delays the recovery actions.
export const STUCK_AFTER_SECONDS = 9 * 60;

interface Phase {
  label: string;
  hint: string;
  /** Backend progress value at which this phase STARTS. */
  at: number;
}

const PHASES: Phase[] = [
  {
    label: "Rozpoznajemy lek",
    hint: "Dopasowujemy substancję czynną i kod ATC",
    at: 0,
  },
  {
    label: "Przeszukujemy źródła naukowe",
    hint: "Publikacje, rejestry leków i bazy laktacyjne",
    at: GENERATION_STARTS_AT,
  },
  {
    label: "Porządkujemy informacje",
    hint: "Łączymy każde stwierdzenie ze źródłem",
    at: 87.5,
  },
  {
    label: "Kończymy raport",
    hint: "Układamy sekcje, przypisy i ostrzeżenia",
    at: 95,
  },
];

function activeIndex(progress: number): number {
  let idx = 0;
  for (let i = 0; i < PHASES.length; i += 1) {
    if (progress >= PHASES[i].at) idx = i;
  }
  return idx;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function ReportProgress({
  progress = 0,
  startedAt,
  onRetry,
  onReportStuck,
  retrying = false,
}: {
  progress?: number;
  startedAt?: string;
  onRetry?: () => void;
  onReportStuck?: () => void;
  retrying?: boolean;
}) {
  const current = activeIndex(progress);
  const next = PHASES[current + 1];
  const ceil = next ? next.at : 100;
  const floor = PHASES[current].at;

  // The impure "now" fallback lives in a useState initializer (allowed to be
  // impure — it runs once), so render stays pure and startMs still follows a
  // late-arriving startedAt.
  const parsedStart = startedAt ? new Date(startedAt).getTime() : NaN;
  const [fallbackStart] = useState(() => Date.now());
  const startMs = Number.isFinite(parsedStart) ? parsedStart : fallbackStart;

  const [elapsed, setElapsed] = useState(() =>
    Math.max(0, Math.round((Date.now() - startMs) / 1000)),
  );
  const [display, setDisplay] = useState(0);
  const [stuckReported, setStuckReported] = useState(false);

  useEffect(() => {
    const tick = () =>
      setElapsed(Math.max(0, Math.round((Date.now() - startMs) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startMs]);

  // Initialized to 0, not Date.now(): a ref initializer is evaluated on every
  // render, and Date.now() during render is impure. The effect below stamps the
  // real time on mount and on every phase change, before any interval tick can
  // read it.
  const stepStartRef = useRef<number>(0);
  useEffect(() => {
    stepStartRef.current = Date.now();
  }, [current]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const tInStep = (Date.now() - stepStartRef.current) / 1000;
      const cap = floor + (ceil - floor) * 0.9;
      const target = floor + (cap - floor) * (1 - Math.exp(-tInStep / 18));
      setDisplay((d) => Math.max(d, Math.min(cap, d + (target - d) * 0.18)));
    }, 200);
    return () => window.clearInterval(id);
  }, [floor, ceil]);

  const pct = Math.round(display);
  const isStuck = elapsed >= STUCK_AFTER_SECONDS;

  const handleReportStuck = () => {
    if (stuckReported || !onReportStuck) return;
    setStuckReported(true);
    onReportStuck();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-border bg-card px-5 py-5 shadow-sm sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
              </span>
              <h2 className="text-sm font-semibold text-foreground">
                Przygotowujemy raport
              </h2>
            </div>
            <p className="text-xs text-muted-foreground">
              {waitingCopy(progress)}
            </p>
            <p className="text-xs text-muted-foreground">
              Pole wyszukiwania pozostało aktywne — możesz w tym czasie
              sprawdzić inny lek.
            </p>
          </div>
          <span
            className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground"
            aria-hidden
          >
            {formatElapsed(elapsed)}
          </span>
        </div>

        <div
          className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label="Postęp generowania raportu"
        >
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>

        <ol className="mt-5 flex flex-col gap-3.5">
          {PHASES.map((phase, i) => {
            const done = i < current;
            const active = i === current;
            return (
              <li key={phase.label} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                  {done ? (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/15 text-brand">
                      <Icon name="check" width={12} height={12} />
                    </span>
                  ) : active ? (
                    <Icon
                      name="loader-circle"
                      width={18}
                      height={18}
                      className="animate-spin text-brand"
                    />
                  ) : (
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                  )}
                </span>
                <div className="flex flex-col gap-0.5">
                  <span
                    className={cn(
                      "text-sm leading-tight transition-colors",
                      done && "text-muted-foreground",
                      active && "font-medium text-foreground",
                      !done && !active && "text-muted-foreground/50",
                    )}
                  >
                    {phase.label}
                  </span>
                  {active && (
                    <span
                      className="text-xs text-muted-foreground"
                      aria-live="polite"
                    >
                      {phase.hint}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {isStuck && (
          <div
            className="mt-5 rounded-lg border border-border bg-muted/40 px-4 py-3"
            role="status"
          >
            <p className="text-sm font-medium text-foreground">
              To trwa dłużej niż zwykle
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Raport nadal powstaje w tle. Możesz spróbować ponownie albo
              zgłosić nam problem.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {onRetry && (
                <Button
                  type="button"
                  size="sm"
                  onClick={onRetry}
                  disabled={retrying}
                >
                  {retrying ? "Ponawiamy…" : "Spróbuj ponownie"}
                </Button>
              )}
              {onReportStuck && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleReportStuck}
                  disabled={stuckReported}
                >
                  {stuckReported ? "Zgłoszono" : "Zgłoś problem"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <ReportSkeleton />
    </div>
  );
}

function ReportSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-hidden>
      {[0, 1, 2].map((card) => (
        <div
          key={card}
          className="animate-pulse rounded-xl border border-border bg-card px-5 py-4 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-muted" />
            <div className="h-3.5 w-40 rounded bg-muted" />
          </div>
          <div className="mt-4 flex flex-col gap-2.5">
            <div className="h-3 w-full rounded bg-muted" />
            <div className="h-3 w-11/12 rounded bg-muted" />
            <div className="h-3 w-4/5 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}
